import { FC } from "react";
import KpiCard from "../components/KpiCard/KpiCard";
import LineChart from "../components/LineChart/LineChart";
import BarChart from "../components/BarChart/BarChart";
import EmptyState from "../components/EmptyState/EmptyState";
import useTrackingAggregate from "../hooks/useTrackingAggregate";
import { autoBucketFor, buildPeriod, type Period } from "../utils/period";
import { EventNames } from "../utils/eventNames";

interface Props {
	period: Period;
	availableEvents: Set<string>;
}

const sumBuckets = (
	buckets: { count: number; distinct_user_count: number | null }[]
): { total: number; distinct: number | null } => {
	let total = 0;
	let distinct: number | null = null;
	for (const b of buckets) {
		total += b.count;
		if (b.distinct_user_count !== null) {
			distinct = (distinct ?? 0) + b.distinct_user_count;
		}
	}
	return { total, distinct };
};

const dayMs = 24 * 60 * 60 * 1000;

const lastNDays = (period: Period, n: number): Period => {
	const from = new Date(period.to.getTime() - n * dayMs);
	return buildPeriod(from, period.to, `${n}d`);
};

const OverviewTab: FC<Props> = ({ period, availableEvents }) => {
	const sessionEvent = EventNames.SESSION_START;
	const sessionAvailable = availableEvents.has(sessionEvent);

	// DAU/WAU/MAU
	const todayP = lastNDays(period, 1);
	const wauP = lastNDays(period, 7);
	const mauP = lastNDays(period, 30);
	const prevTodayP = buildPeriod(
		new Date(todayP.from.getTime() - dayMs),
		todayP.from,
		"prev"
	);
	const prevWauP = buildPeriod(
		new Date(wauP.from.getTime() - 7 * dayMs),
		wauP.from,
		"prev"
	);
	const prevMauP = buildPeriod(
		new Date(mauP.from.getTime() - 30 * dayMs),
		mauP.from,
		"prev"
	);

	const dau = useTrackingAggregate(
		{
			event: sessionEvent,
			from: todayP.from,
			to: todayP.to,
			groupBy: "day"
		},
		{ enabled: sessionAvailable }
	);
	const wau = useTrackingAggregate(
		{
			event: sessionEvent,
			from: wauP.from,
			to: wauP.to,
			groupBy: "week"
		},
		{ enabled: sessionAvailable }
	);
	const mau = useTrackingAggregate(
		{
			event: sessionEvent,
			from: mauP.from,
			to: mauP.to,
			groupBy: "month"
		},
		{ enabled: sessionAvailable }
	);
	const dauPrev = useTrackingAggregate(
		{
			event: sessionEvent,
			from: prevTodayP.from,
			to: prevTodayP.to,
			groupBy: "day"
		},
		{ enabled: sessionAvailable }
	);
	const wauPrev = useTrackingAggregate(
		{
			event: sessionEvent,
			from: prevWauP.from,
			to: prevWauP.to,
			groupBy: "week"
		},
		{ enabled: sessionAvailable }
	);
	const mauPrev = useTrackingAggregate(
		{
			event: sessionEvent,
			from: prevMauP.from,
			to: prevMauP.to,
			groupBy: "month"
		},
		{ enabled: sessionAvailable }
	);

	const dauVal = dau.data ? sumBuckets(dau.data.buckets).distinct : null;
	const wauVal = wau.data ? sumBuckets(wau.data.buckets).distinct : null;
	const mauVal = mau.data ? sumBuckets(mau.data.buckets).distinct : null;
	const dauPrevVal = dauPrev.data
		? sumBuckets(dauPrev.data.buckets).distinct
		: null;
	const wauPrevVal = wauPrev.data
		? sumBuckets(wauPrev.data.buckets).distinct
		: null;
	const mauPrevVal = mauPrev.data
		? sumBuckets(mauPrev.data.buckets).distinct
		: null;

	const stickiness =
		dauVal !== null && mauVal !== null && mauVal > 0 ? dauVal / mauVal : null;
	const stickinessPrev =
		dauPrevVal !== null && mauPrevVal !== null && mauPrevVal > 0
			? dauPrevVal / mauPrevVal
			: null;

	// Sessions + events totals across the user-selected period
	const sessions = useTrackingAggregate(
		{
			event: sessionEvent,
			from: period.from,
			to: period.to,
			groupBy: autoBucketFor(period)
		},
		{ enabled: sessionAvailable }
	);

	const platformSplit = useTrackingAggregate(
		{
			event: sessionEvent,
			from: period.from,
			to: period.to,
			groupBy: "os"
		},
		{ enabled: sessionAvailable }
	);

	if (!sessionAvailable) {
		return (
			<EmptyState
				title="No session data yet"
				description={`Tracking event "${sessionEvent}" hasn't been emitted by any client yet. Once mobile starts firing it, this view will populate.`}
				tone="warn"
			/>
		);
	}

	const isRollup = sessions.data?.data_source === "rollup";

	const sessionsTotal = sessions.data
		? sumBuckets(sessions.data.buckets).total
		: null;

	const platformSeries: { label: string; value: number }[] = (
		platformSplit.data?.buckets ?? []
	).map((b) => ({
		label: b.bucket,
		value: b.count
	}));

	return (
		<div className="space-y-8">
			<div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
				<KpiCard
					label="DAU (today)"
					loading={dau.isLoading}
					error={dau.isError}
					onRetry={() => dau.refetch()}
					value={dauVal !== null ? dauVal.toLocaleString() : null}
					currentValue={dauVal}
					previousValue={dauPrevVal}
				/>
				<KpiCard
					label="WAU (last 7d)"
					loading={wau.isLoading}
					error={wau.isError}
					onRetry={() => wau.refetch()}
					value={wauVal !== null ? wauVal.toLocaleString() : null}
					currentValue={wauVal}
					previousValue={wauPrevVal}
				/>
				<KpiCard
					label="MAU (last 30d)"
					loading={mau.isLoading}
					error={mau.isError}
					onRetry={() => mau.refetch()}
					value={mauVal !== null ? mauVal.toLocaleString() : null}
					currentValue={mauVal}
					previousValue={mauPrevVal}
				/>
				<KpiCard
					label="Stickiness (DAU/MAU)"
					loading={dau.isLoading || mau.isLoading}
					value={
						stickiness !== null ? `${(stickiness * 100).toFixed(1)}%` : null
					}
					currentValue={stickiness}
					previousValue={stickinessPrev}
				/>
			</div>

			<div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
				<KpiCard
					label="Total sessions"
					loading={sessions.isLoading}
					error={sessions.isError}
					value={sessionsTotal !== null ? sessionsTotal.toLocaleString() : null}
					footer={
						isRollup ? (
							<p className="text-xs text-muted-foreground">Updated hourly</p>
						) : null
					}
				/>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<p className="text-sm text-muted-foreground mb-3">Platform split</p>
					{platformSplit.isLoading ? (
						<div className="h-12 w-full rounded animate-pulse bg-zinc-200" />
					) : platformSeries.length === 0 ? (
						<p className="text-sm text-muted-foreground">No data yet</p>
					) : (
						<BarChart
							data={platformSeries}
							ariaLabel="Sessions by operating system"
							valueLabel="sessions"
						/>
					)}
				</div>
			</div>

			<div>
				<h3 className="font-heading text-base font-medium text-foreground mb-3">
					Daily active users
				</h3>
				<div className="rounded-xl border border-border bg-white shadow-sm p-4">
					<LineChart
						ariaLabel="Daily active users over time"
						yLabel="Unique users"
						series={[
							{
								label: period.label,
								color: "var(--color-primary)",
								data: (sessions.data?.buckets ?? []).map((b) => ({
									x: b.bucket,
									y: b.distinct_user_count ?? b.count
								}))
							}
						]}
					/>
					{isRollup ? (
						<p className="text-xs text-muted-foreground mt-3">
							Updated hourly · served from rollup
						</p>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default OverviewTab;
